
import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Stack, Button, IconButton,
    TextField, Tooltip, CircularProgress, Alert, alpha,
    Badge, useMediaQuery, useTheme
} from '@mui/material';
import {
    EditOutlined, CloseOutlined, FlightTakeoff, Hotel,
    HealthAndSafety, AccessTime, Person2Outlined, SendOutlined,
    RefreshOutlined, ArrowBackIosNew
} from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { DossierPreviewCanvas } from '../components/DossierPreviewCanvas';
import apiClient from '../../../services/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNotificationStore } from '../../../store/useNotificationStore';

// ── Helpers de style ─────────────────────────────────────────────────────────
const TYPE_ICON_SMALL = {
    BILLET: <FlightTakeoff sx={{ fontSize: 11 }} />,
    RESERVATION_HOTEL: <Hotel sx={{ fontSize: 11 }} />,
    ASSURANCE: <HealthAndSafety sx={{ fontSize: 11 }} />,
};
const TYPE_COLOR = { BILLET: '#4f8ef7', RESERVATION_HOTEL: '#c9a84c', ASSURANCE: '#6dd9a8' };
const TYPE_LABEL = { BILLET: 'Vol', RESERVATION_HOTEL: 'Hôtel', ASSURANCE: 'Assurance' };

// ── Composant Item de Liste ──────────────────────────────────────────────────
function DossierListItem({ dossier, isActive, onClick }) {
    const initials = dossier.client?.nomComplet
        ?.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase() ?? '??';

    const timeAgo = (() => {
        if (!dossier.dateCreation) return "-";
        const h = Math.floor((Date.now() - new Date(dossier.dateCreation)) / 3600000);
        if (h < 1) return "À l'instant";
        if (h < 24) return `${h}h`;
        return `${Math.floor(h / 24)}j`;
    })();

    return (
        <Box
            onClick={onClick}
            sx={{
                p: 2.5, borderRadius: '16px', cursor: 'pointer',
                border: '1px solid',
                transition: 'all 0.22s ease',
                bgcolor: isActive ? '#1a1a2e' : 'white',
                borderColor: isActive ? '#1a1a2e' : 'rgba(26,26,46,0.07)',
                '&:hover': {
                    borderColor: isActive ? '#1a1a2e' : 'rgba(201,168,76,0.4)',
                    transform: isActive ? 'none' : 'translateY(-1px)',
                },
            }}
        >
            <Stack direction="row" spacing={1.8}>
                <Box sx={{ width: 40, height: 40, borderRadius: '11px', flexShrink: 0, bgcolor: isActive ? 'rgba(201,168,76,0.15)' : 'rgba(26,26,46,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', color: isActive ? '#c9a84c' : 'rgba(26,26,46,0.45)' }}>{initials}</Typography>
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: isActive ? '#c9a84c' : 'rgba(26,26,46,0.4)' }}>#{dossier.referenceCode}</Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: isActive ? 'rgba(255,255,255,0.35)' : 'rgba(26,26,46,0.35)' }}>{timeAgo}</Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: isActive ? 'white' : '#1a1a2e', mb: 1, noWrap: true }}>
                        {dossier.client?.nomComplet}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {dossier.prestations?.map((p, i) => (
                            <Box key={i} sx={{ px: 0.8, py: 0.2, borderRadius: '4px', bgcolor: isActive ? 'rgba(255,255,255,0.1)' : `${TYPE_COLOR[p.type]}15`, color: isActive ? 'white' : TYPE_COLOR[p.type], fontSize: '0.6rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {TYPE_ICON_SMALL[p.type]} {TYPE_LABEL[p.type]}
                            </Box>
                        ))}
                    </Stack>
                </Box>
            </Stack>
        </Box>
    );
}

// ── Page Principale ──────────────────────────────────────────────────────────
export function ValidationPage() {
    const theme = useTheme();

    const queryClient = useQueryClient();
    const showNotify = useNotificationStore(state => state.show);

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [selectedId, setSelectedId] = useState(null);
    const [showMobileDetail, setShowMobileDetail] = useState(false);

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['pending-validation', 'BROUILLON'],
        queryFn: async () => {
            const response = await apiClient.get('/api/orders', {
                params: {
                    statut: 'BROUILLON'
                }
            });
            return response.data.content;
        },
    });


    // La Mutation de Validation
    const mutation = useMutation({
        mutationFn: async (id) => {
            return await apiClient.post(`/api/orders/${id}/validate`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['pending-validation']);

            showNotify({
                type: 'success',
                title: 'Dossier Validé',
                message: 'Le dossier a été transmis à la comptabilité avec succès.'
            });

            setSelectedId(null);
            setShowMobileDetail(false);
        },
        onError: (error) => {
            showNotify({
                type: 'error',
                title: 'Échec de validation',
                message: error.response?.data?.message || "Une erreur est survenue."
            });
        }
    });

    const handleValidate = () => {
        if (selectedId) {
            mutation.mutate(selectedId);
        }
    };

    const dossiers = data || [];

    // Sélection automatique du premier dossier
    useEffect(() => {
        if (dossiers.length > 0 && !selectedId) {
            setSelectedId(dossiers[0].id);
        }
    }, [dossiers, selectedId]);

    const handleSelectDossier = (id) => {
        setSelectedId(id);
        if (isMobile) {
            setShowMobileDetail(true);
        }
    };

    const selected = dossiers.find(d => d.id === selectedId);

    if (isLoading) return (
        <Box sx={{ display: 'flex', height: '80vh', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: '#c9a84c' }} />
        </Box>
    );

    return (
        <Box sx={{
            height: { xs: 'auto', md: 'calc(100vh - 80px)' },
            display: 'flex',
            overflow: 'hidden',
            bgcolor: '#f4f2ec',
            mx: { xs: -2, sm: -4 },
            mt: { xs: -2, sm: -4 },
            position: 'relative'
        }}>

            {/* COLONNE GAUCHE (LISTE) */}
            <Box sx={{
                width: { xs: '100%', md: 320, lg: 380 },
                flexShrink: 0,
                display: { xs: showMobileDetail ? 'none' : 'flex', md: 'flex' },
                flexDirection: 'column',
                bgcolor: '#f7f5f0',
                borderRight: '1px solid rgba(26,26,46,0.07)'
            }}>
                <Box sx={{ px: 3, pt: 3.5, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5, alignItems: 'center' }}>
                        <Typography sx={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.35rem', fontWeight: 700 }}>File d'attente</Typography>
                        <Box sx={{
                            bgcolor: '#1a1a2e', px: 1.2, py: 0.3, borderRadius: '6px',
                            fontSize: '0.75rem', color: '#c9a84c', fontWeight: 800
                        }}>
                            {dossiers.length}
                        </Box>
                    </Box>
                    <IconButton onClick={() => refetch()} size="small">
                        <RefreshOutlined sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>

                <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, pb: 3 }}>
                    {dossiers.length === 0 ? (
                        <Typography sx={{ textAlign: 'center', mt: 4, fontSize: '0.8rem', color: 'rgba(26,26,46,0.4)' }}>Aucun dossier en attente</Typography>
                    ) : (
                        <Stack spacing={1.5}>
                            {dossiers.map(d => (
                                <DossierListItem
                                    key={d.id}
                                    dossier={d}
                                    isActive={selectedId === d.id}
                                    onClick={() => handleSelectDossier(d.id)}
                                />
                            ))}
                        </Stack>
                    )}
                </Box>
            </Box>

            {/* ZONE DROITE (DÉTAIL) */}
            <Box sx={{
                flexGrow: 1,
                minWidth: 0,
                display: { xs: showMobileDetail ? 'flex' : 'none', md: 'flex' },
                flexDirection: 'column',
                bgcolor: '#f4f2ec'
            }}>
                {selected ? (
                    <>
                        <Box sx={{
                            px: { xs: 2, md: 4 },
                            py: 2,
                            bgcolor: 'rgba(247,245,240,0.95)',
                            borderBottom: '1px solid rgba(26,26,46,0.07)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                {isMobile && (
                                    <IconButton onClick={() => setShowMobileDetail(false)} sx={{ mr: 1 }}>
                                        <ArrowBackIosNew sx={{ fontSize: 18 }} />
                                    </IconButton>
                                )}
                                <Box>
                                    <Typography sx={{ fontFamily: "'Cormorant Garamond', serif", fontSize: { xs: '1rem', md: '1.2rem' }, fontWeight: 700 }}>
                                        {selected.client?.nomComplet}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.72rem', color: '#c9a84c', fontWeight: 700 }}>#{selected.referenceCode}</Typography>
                                </Box>
                            </Stack>
                            {selected.statut != "CONFIRME" && (
                                <Button
                                    variant="contained"
                                    startIcon={mutation.isPending ? <CircularProgress size={14} color="#c9a84c" /> : <CheckCircleIcon />}
                                    size={isMobile ? "small" : "medium"}
                                    onClick={handleValidate}
                                    disabled={mutation.isPending}
                                    sx={{
                                        bgcolor: '#1a1a2e', color: '#c9a84c', textTransform: 'none',
                                        borderRadius: '10px', fontWeight: 700
                                    }}
                                >
                                    {mutation.isPending ? "Traitement..." : isMobile ? "Valider" : "Valider & Transmettre"}
                                </Button>
                            )}
                        </Box>

                        <Box sx={{ flexGrow: 1, overflowY: 'auto', px: { xs: 2, md: 4 }, py: 3 }}>
                            <DossierPreviewCanvas dossier={selected} />
                        </Box>
                    </>
                ) : (
                    <Box sx={{ display: 'flex', flexGrow: 1, alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ color: 'rgba(26,26,46,0.3)' }}>Sélectionnez un dossier pour voir les détails</Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
}